import { DataGrid, type GridColDef, SearchInput } from "@msys/ui";
import { Checkbox, Stack } from "@mui/material";
import type {
  GridGroupNode,
  GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { OfferedProjectType } from "../../../../clients/graphqlTypes.js";
import {
  filterCategories,
  removeSubcategoriesOfDeletedParent,
} from "../../categories/helpers.js";
import { useProjectTypes } from "../../projects/useProjectTypes.js";
import type { CategoryFragment } from "../../categories/useCategories.generated.js";
import { OFFERED_PROJECT_TYPES } from "./projectTypes.js";

export const matrixCellKey = (
  categoryId: string,
  projectType: OfferedProjectType
) => `${categoryId}|${projectType}`;

export const parseMatrixCellKey = (
  key: string
): { categoryId: string; projectType: OfferedProjectType } => {
  const sepIdx = key.indexOf("|");
  return {
    categoryId: key.slice(0, sepIdx),
    projectType: key.slice(sepIdx + 1) as OfferedProjectType,
  };
};

interface MatrixRow {
  id: string;
  name: string;
  path: string[];
}

export interface OfferedServicesMatrixProps {
  categories: CategoryFragment[];
  declaredCategoryIds: ReadonlySet<string>;
  /** Cells keyed by `matrixCellKey(categoryId, projectType)`. */
  cellSelections: ReadonlySet<string>;
  /** Callers must filter cells whose category just left the declared set. */
  onDeclaredChange: (next: readonly string[]) => void;
  onToggleCell: (categoryId: string, projectType: OfferedProjectType) => void;
  initiallyExpandedRootIds?: ReadonlySet<string>;
}

export const OfferedServicesMatrix = ({
  categories,
  declaredCategoryIds,
  cellSelections,
  onDeclaredChange,
  onToggleCell,
  initiallyExpandedRootIds,
}: OfferedServicesMatrixProps) => {
  const { t } = useTranslate(["Global", "OrganisationProfile"]);
  const { projectTypeLabels } = useProjectTypes();

  const [search, setSearch] = React.useState("");

  const usableCategories = React.useMemo(
    () => removeSubcategoriesOfDeletedParent(categories, categories),
    [categories]
  );

  // `path` is the chain of NAMES from root to self so the default tree-data
  // grouping cell can render the human-readable label without a custom cell.
  const allRows = React.useMemo<MatrixRow[]>(() => {
    if (usableCategories.length === 0) return [];
    const byId = new Map(usableCategories.map(c => [c.id, c]));
    const namePathOf = (id: string): string[] => {
      const out: string[] = [];
      let cur: string | undefined = id;
      while (cur) {
        const node = byId.get(cur);
        if (!node) break;
        out.unshift(node.name);
        cur = node.parentId ?? undefined;
      }
      return out;
    };
    return usableCategories.map(c => ({
      id: c.id,
      name: c.name,
      path: namePathOf(c.id),
    }));
  }, [usableCategories]);

  const rows = React.useMemo<MatrixRow[]>(() => {
    if (!search.trim()) return allRows;
    const matched = filterCategories(usableCategories, search);
    const visibleIds = new Set(matched.map(c => c.id));
    return allRows.filter(r => visibleIds.has(r.id));
  }, [allRows, usableCategories, search]);

  // DataGrid's `node.groupingKey` is the row's name, not its id — translate.
  const initiallyExpandedNames = React.useMemo(() => {
    if (!initiallyExpandedRootIds || initiallyExpandedRootIds.size === 0) {
      return new Set<string>();
    }
    const names = new Set<string>();
    const byId = new Map(allRows.map(r => [r.id, r]));
    for (const id of initiallyExpandedRootIds) {
      const row = byId.get(id);
      if (row) names.add(row.name);
    }
    return names;
  }, [allRows, initiallyExpandedRootIds]);

  // Stable identity — a new prop reference makes MUI re-evaluate this for
  // every node, reverting user-driven expansion on unrelated re-renders.
  const isGroupExpandedByDefault = React.useCallback(
    (node: GridGroupNode) =>
      node.depth === 0 && initiallyExpandedNames.has(String(node.groupingKey)),
    [initiallyExpandedNames]
  );

  const rowSelectionModel = React.useMemo<GridRowSelectionModel>(
    () => ({
      type: "include",
      ids: new Set(declaredCategoryIds),
    }),
    [declaredCategoryIds]
  );

  const handleRowSelectionModelChange = React.useCallback(
    (next: GridRowSelectionModel) => {
      onDeclaredChange(Array.from(next.ids).map(String));
    },
    [onDeclaredChange]
  );

  const projectTypeColumns = React.useMemo<GridColDef<MatrixRow>[]>(
    () =>
      OFFERED_PROJECT_TYPES.map(projectType => ({
        field: projectType,
        headerName: projectTypeLabels[projectType],
        sortable: false,
        groupable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        width: 90,
        renderCell: ({ row }) => (
          <MatrixCheckboxCell
            row={row}
            projectType={projectType}
            cellSelections={cellSelections}
            declaredCategoryIds={declaredCategoryIds}
            onToggleCell={onToggleCell}
          />
        ),
      })),
    [projectTypeLabels, cellSelections, declaredCategoryIds, onToggleCell]
  );

  const groupingColDef = React.useMemo<GridColDef<MatrixRow>>(
    () => ({
      field: "name",
      headerName: t("Category", { ns: "OrganisationProfile" }),
      flex: 1,
      minWidth: 220,
      hideable: false,
      sortable: false,
    }),
    [t]
  );

  return (
    <Stack spacing={2}>
      <SearchInput
        placeholder={t("Search categories", { ns: "OrganisationProfile" })}
        value={search}
        onChangeSearchTerm={value => setSearch(value)}
        size="extra-small"
        sx={{
          transition: "ease-in-out 250ms max-width",
          maxWidth: "100%",
          ":not(:focus-within)": {
            maxWidth: 320,
          },
        }}
      />
      <DataGrid
        rows={rows}
        columns={projectTypeColumns}
        treeData
        getTreeDataPath={getTreeDataPath}
        groupingColDef={groupingColDef}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionPropagation={{ descendants: false, parents: false }}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={handleRowSelectionModelChange}
        autoHeight
        density="compact"
        hideFooter
        pagination={false}
        disableColumnFilter
        disableColumnMenu
        // Virtualisation needs layout measurements jsdom doesn't provide;
        // taxonomy is ~300 rows so rendering all of them is cheap.
        disableVirtualization
        isGroupExpandedByDefault={isGroupExpandedByDefault}
      />
    </Stack>
  );
};

const getTreeDataPath = (row: MatrixRow) => row.path;

interface MatrixCheckboxCellProps {
  row: MatrixRow;
  projectType: OfferedProjectType;
  cellSelections: ReadonlySet<string>;
  declaredCategoryIds: ReadonlySet<string>;
  onToggleCell: (categoryId: string, projectType: OfferedProjectType) => void;
}

const MatrixCheckboxCell = ({
  row,
  projectType,
  cellSelections,
  declaredCategoryIds,
  onToggleCell,
}: MatrixCheckboxCellProps) => {
  const isDeclared = declaredCategoryIds.has(row.id);
  const isChecked = cellSelections.has(matrixCellKey(row.id, projectType));

  return (
    <Checkbox
      size="small"
      disabled={!isDeclared}
      checked={isChecked}
      // Stops DataGrid's cell-click from collapsing the parent tree group.
      onClick={e => e.stopPropagation()}
      onChange={() => onToggleCell(row.id, projectType)}
      inputProps={{
        "aria-label": `${row.name} ${projectType}`,
      }}
    />
  );
};
